if (!customElements.get("media-gallery")) {
  customElements.define(
    "media-gallery",
    class MediaGallery extends HTMLElement {
      constructor() {
        super();

        this.selectors = {
          viewer: '[id^="GalleryViewer"]',
          thumbnails: '[id^="GalleryThumbnails"]',
          mediaList: '[id^="Slider-Gallery"]',
          mediaItems: [".product__media-item"],
        };

        this.sliderInstance = false;
        this.thumbsInstance = false;
      }

      connectedCallback() {
        this.init();
      }

      init() {
        this.elements = window.FoxTheme.utils.queryDomNodes(
          this.selectors,
          this,
        );
        this.mediaLayout = this.dataset.mediaLayout;
        this.onlyImage = this.dataset.onlyImage === "true";
        this.enableDesktopSlider = this.dataset.enableDesktopSlider === "true";
        this.enableMobileThumbnails =
          this.dataset.enableMobileThumbnails === "true";
        this.enableImageZoom = this.dataset.enableImageZoom === "true";
        this.context = this.dataset.context;
        this.setSliderOptions();

        const mql = window.matchMedia(FoxTheme.config.mediaQueryMobile);
        mql.onchange = this.updateMediaLayout.bind(this);
        this.updateMediaLayout();

        if (this.enableImageZoom) {
          this.initImageZoom();
        }
      }

      setSliderOptions() {
        const mediaItemGap = parseInt(this.dataset.mediaItemGap);
        const mediaItemGapMobile = parseInt(this.dataset.mediaItemGapMobile);

        this.sliderOptions = {
          init: false,
          slidesPerView: this.enableMobileThumbnails ? 1 : "auto",
          spaceBetween: mediaItemGapMobile,
          loop: true,
          grabCursor: true,
          allowTouchMove: true,
          autoHeight: true,
          breakpoints: {
            768: {
              spaceBetween: mediaItemGap,
            },
          },
          navigation: {
            nextEl: this.querySelector(".swiper-button-next"),
            prevEl: this.querySelector(".swiper-button-prev"),
          },
          pagination: {
            el: this.querySelector(".swiper-pagination"),
            clickable: true,
            type: this.elements.mediaItems.length > 10 ? "fraction" : "bullets",
          },
          threshold: 2,
        };

        this.thumbsOptions = {
          slidesPerView: 4,
          breakpoints: {
            461: {
              slidesPerView: 5,
              direction: "horizontal",
            },
          },
          spaceBetween: mediaItemGap,
          loop: false,
          freeMode: true,
          watchSlidesProgress: true,
          threshold: 2,
          navigation: {
            nextEl: this.querySelector(
              ".product__media-gallery-thumbails-wrap .swiper-button-next",
            ),
            prevEl: this.querySelector(
              ".product__media-gallery-thumbails-wrap .swiper-button-prev",
            ),
          },
        };

        switch (this.mediaLayout) {
          case "vertical-carousel":
            this.thumbsOptions.breakpoints = {
              ...this.thumbsOptions.breakpoints,
              1024: {
                direction: "vertical",
                slidesPerView: "auto",
              },
            };
            break;
          case "carousel":
            this.thumbsOptions.breakpoints = {
              ...this.thumbsOptions.breakpoints,
              1024: {
                slidesPerView: 7,
              },
            };
            break;
        }
      }

      updateMediaLayout() {
        if (FoxTheme.config.mqlMobile) {
          this.initSlider();
        } else {
          if (this.enableDesktopSlider) {
            this.initSlider();
          } else {
            this.destroySlider();
          }
        }
      }

      initSlider() {
        if (typeof this.sliderInstance !== "object") {
          if (
            (this.enableDesktopSlider || this.enableMobileThumbnails) &&
            this.elements.thumbnails
          ) {
            this.thumbsInstance = new window.FoxTheme.Carousel(
              this.elements.thumbnails,
              this.thumbsOptions,
            );
            this.thumbsInstance.init();

            this.sliderOptions.thumbs = {
              swiper: this.thumbsInstance.slider,
              autoScrollOffset: 2,
            };
          }

          this.sliderInstance = new window.FoxTheme.Carousel(
            this.elements.viewer,
            this.sliderOptions,
            [FoxTheme.Swiper.Thumbs],
          );
          this.sliderInstance.init();

          this.handleSliderAfterInit();
          this.handleSlideChange();

          this.sliderInstance.slider.init();
        }
      }

      destroySlider() {
        if (typeof this.sliderInstance === "object") {
          this.sliderInstance.slider.destroy();
          this.sliderInstance = false;
        }
      }

      destroyThumbs() {
        if (this.thumbsInstance && this.thumbsInstance.slider) {
          try {
            this.thumbsInstance.slider.destroy(true, true);
          } catch (e) {
            console.warn(e);
          }
          this.thumbsInstance = false;
        }
      }

      destroyImageZoom() {
        if (this.lightbox) {
          try {
            if (typeof this.lightbox.destroy === "function") {
              this.lightbox.destroy();
            }
          } catch (e) {
            console.warn(e);
          }
          this.lightbox = null;
        }
      }

      teardown() {
        this.destroySlider();
        this.destroyThumbs();
        this.destroyImageZoom();
      }

      initThumbsSlider() {
        if (typeof this.thumbsInstance !== "object") {
          this.thumbsInstance = new window.FoxTheme.Carousel(
            this.selectors.thumbnails,
            this.thumbsOptions,
          );
          this.thumbsInstance.init();
        }
      }

      initImageZoom() {
        this.destroyImageZoom();

        this._zoomDataSource = [];
        let dataSource = this._zoomDataSource;
        const allMedia = [
          ...this.querySelectorAll(
            ".product__media-item:not(.swiper-slide-duplicate)",
          ),
        ];
        const getMediaContentHtml = (mediaEl) => {
          const customMedia =
            mediaEl.querySelector("video-element") ||
            mediaEl.querySelector("product-model");
          if (customMedia) return customMedia.outerHTML;

          const contentEl =
            mediaEl.querySelector(".product__media") ||
            mediaEl.querySelector(".product__media-item-container");
          return contentEl ? contentEl.innerHTML : "";
        };

        if (allMedia) {
          allMedia.forEach((media) => {
            switch (media.dataset.mediaType) {
              case "model":
              case "video":
              case "external_video": {
                const mediaHtml = getMediaContentHtml(media);
                if (!mediaHtml) break;

                dataSource.push({
                  id: media.dataset.mediaIndex,
                  html: `<div class="pswp__item--${media.dataset.mediaType}">${mediaHtml}</div>`,
                  mediaType: media.dataset.mediaType,
                  mediaId: media.dataset.mediaId,
                });
                break;
              }
              case "image":
                dataSource.push({
                  id: media.dataset.mediaIndex,
                  src: media.dataset.src,
                  width: media.dataset.pswpWidth,
                  height: media.dataset.pswpHeight,
                  mediaType: media.dataset.mediaType,
                  mediaId: media.dataset.mediaId,
                });
                break;
            }
          });
        }

        this.lightbox = new window.FoxTheme.PhotoSwipeLightbox({
          dataSource: dataSource,
          pswpModule: window.FoxTheme.PhotoSwipe,
          bgOpacity: 1,
          arrowPrev: false,
          arrowNext: false,
          zoom: false,
          close: false,
          counter: false,
          preloader: false,
        });

        this.lightbox.addFilter("thumbEl", (thumbEl, { id }, index) => {
          if (this.sliderInstance && this.sliderInstance.slider) {
            const { slides, activeIndex } = this.sliderInstance.slider;
            if (slides[activeIndex]) {
              const el = slides[activeIndex].querySelector("img");
              if (el) {
                return el;
              }
            }
          }

          return thumbEl;
        });

        this.lightbox.addFilter(
          "placeholderSrc",
          (placeholderSrc, { data: { id } }) => {
            if (this.sliderInstance && this.sliderInstance.slider) {
              const { slides, activeIndex } = this.sliderInstance.slider;
              if (slides[activeIndex]) {
                const el = slides[activeIndex].querySelector("img");
                if (el) {
                  return el.src;
                }
              }
            }

            return placeholderSrc;
          },
        );

        this.lightbox.on("change", () => {
          window.pauseAllMedia(this);
          if (this.sliderInstance && this.sliderInstance.slider) {
            const { currIndex } = this.lightbox.pswp;
            const currentSlideData = this.lightbox.pswp.currSlide.data;
            const swiper = this.sliderInstance.slider;

            // Determine the correct swiper real-slide index to navigate to.
            // dataSource entries added for the Vizion view360 slide carry
            // an `isVizionSlide` flag so we can locate the right DOM slide.
            const realSlides = Array.from(
              this.querySelectorAll(
                ".product__media-item:not(.swiper-slide-duplicate)",
              ),
            );

            let targetRealIndex = currIndex;

            if (currentSlideData.isVizionSlide) {
              // Find the Vizion-injected slide by its unique class
              const vizIdx = realSlides.findIndex((s) =>
                s.classList.contains("vz-media-view360"),
              );
              if (vizIdx !== -1) {
                targetRealIndex = vizIdx;
              }
            } else if (currentSlideData.mediaId) {
              // For normal slides match by mediaId, but skip the Vizion clone
              // (it shares the same mediaId as the first image)
              const foundIdx = realSlides.findIndex(
                (s) =>
                  s.dataset.mediaId === currentSlideData.mediaId &&
                  !s.classList.contains("vz-media-view360"),
              );
              if (foundIdx !== -1) {
                targetRealIndex = foundIdx;
              }
            }

            if (swiper.realIndex !== targetRealIndex) {
              swiper.slideToLoop(targetRealIndex, 100, false);
            }
          }
        });

        this.lightbox.on("pointerDown", (e) => {
          if (this.lightbox.pswp.currSlide.data.mediaType != "image") {
            e.preventDefault();
          }
        });

        this.lightbox.on("uiRegister", () => {
          if (!this.onlyImage) {
            this.lightbox.pswp.ui.registerElement({
              name: "next",
              ariaLabel: "Next slide",
              order: 3,
              isButton: true,
              appendTo: "root",
              html: '<svg class="pswp-icon-next flip-x" viewBox="0 0 100 100"><path d="M 10,50 L 60,100 L 65,90 L 25,50  L 65,10 L 60,0 Z"></path></svg>',
              onClick: (event, el) => {
                this.lightbox.pswp.next();
              },
            });
            this.lightbox.pswp.ui.registerElement({
              name: "prev",
              ariaLabel: "Previous slide",
              order: 1,
              isButton: true,
              appendTo: "root",
              html: '<svg class="pswp-icon-prev rtl-flip-x" viewBox="0 0 100 100"><path d="M 10,50 L 60,100 L 65,90 L 25,50  L 65,10 L 60,0 Z"></path></svg>',
              onClick: (event, el) => {
                this.lightbox.pswp.prev();
              },
            });
          }
          this.lightbox.pswp.ui.registerElement({
            name: "close-zoom",
            ariaLabel: "Close zoom image",
            order: 2,
            isButton: true,
            html: '<svg xmlns="http://www.w3.org/2000/svg" aria-hidden="true" focusable="false" role="presentation" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>',
            onClick: (event, el) => {
              this.lightbox.pswp.close();
            },
          });
        });

        this.lightbox.on("contentActivate", ({ content }) => {
          const el = content.element || content.slide?.container;
          if (!el) return;
          el.querySelectorAll(".vz-mobile-3d-btn").forEach((btn) => {
            if (btn._vzClickBound) return;
            btn._vzClickBound = true;
            btn.addEventListener("click", (e) => {
              e.preventDefault();
              e.stopPropagation();
              const url = btn.dataset.redirectUrl;
              if (url) window.location.href = url;
            });
          });
        });

        this.lightbox.init();

        FoxTheme.utils.addEventDelegate({
          selector: ".js-photoswipe--zoom",
          context: this,
          handler: (e, media) => {
            const parentSlide = media.closest(".product__media-item");
            const isVizionSlide =
              parentSlide && parentSlide.classList.contains("vz-media-view360");

            let index = -1;

            if (isVizionSlide) {
              // Look for an existing Vizion entry in dataSource
              index = dataSource.findIndex((item) => item.isVizionSlide);

              // If not found, dynamically build one and append it
              if (index === -1) {
                const mediaType = parentSlide.dataset.mediaType || "model";
                const contentEl =
                  parentSlide.querySelector(".product__media-item-container") ||
                  parentSlide.querySelector(".product__media") ||
                  parentSlide;

                const newEntry = {
                  id: String(dataSource.length),
                  mediaType: mediaType,
                  mediaId: parentSlide.dataset.mediaId,
                  isVizionSlide: true,
                  html: `<div class="pswp__item--${mediaType}">${contentEl.innerHTML}</div>`,
                };

                dataSource.push(newEntry);
                index = dataSource.length - 1;
              }
            } else {
              // Normal slide: find by mediaId, excluding any Vizion entry
              const mediaId = parentSlide
                ? parentSlide.dataset.mediaId
                : media.dataset.mediaId;

              index = dataSource.findIndex(
                (item) => item.mediaId === mediaId && !item.isVizionSlide,
              );

              // Fallback to data-media-index
              if (index === -1) {
                index = Number(media.dataset.mediaIndex) || 0;
              }
            }

            // Clamp to dataSource bounds
            if (index >= dataSource.length) {
              index = dataSource.length - 1;
            }
            if (index < 0) index = 0;

            this.lightbox.loadAndOpen(index);
          },
        });
      }

      handleSliderAfterInit() {
        this.sliderInstance.slider.on("afterInit", (swiper) => {
          const { slides, activeIndex } = swiper;

          if (slides[activeIndex]) {
            const isModelMediaType =
              slides[activeIndex].dataset.mediaType === "model";
            this.toggleSliderDraggableState(!isModelMediaType);
          }
        });
      }

      handleSlideChange() {
        this.sliderInstance.slider.on("realIndexChange", (swiper) => {
          const { slides, activeIndex, thumbs } = swiper;

          if (thumbs.swiper) {
            thumbs.swiper.slideTo(activeIndex);
          }

          if (slides[activeIndex]) {
            this.playActiveMedia(slides[activeIndex]);

            const isModelMediaType =
              slides[activeIndex].dataset.mediaType === "model";
            this.toggleSliderDraggableState(!isModelMediaType);
          }
        });
      }

      toggleSliderDraggableState(isDraggable) {
        if (this.sliderInstance.slider.allowTouchMove !== isDraggable) {
          this.sliderInstance.slider.allowTouchMove = isDraggable;
        }
      }

      playActiveMedia(selected) {
        const deferredMedia = selected.querySelector("product-model");
        if (deferredMedia) deferredMedia.loadContent(false);
      }

      setActiveMedia(variant) {
        if (
          !variant ||
          !variant.hasOwnProperty("featured_media") ||
          !variant.featured_media
        )
          return;

        const featuredId = String(variant.featured_media.id);

        if (this.sliderInstance && this.sliderInstance.slider) {
          const slides = Array.from(this.sliderInstance.slider.slides).filter(
            (slide) => !slide.classList.contains("swiper-slide-duplicate"),
          );
          let targetIndex = slides.findIndex(
            (slide) => String(slide.dataset.mediaId) === featuredId,
          );
          if (targetIndex < 0) {
            targetIndex = 0;
          }
          this.sliderInstance.slider.slideToLoop(targetIndex);
        } else {
          this.sortMediaItems(variant);
        }
      }

      sortMediaItems(variant) {
        let newMedias = this.elements.mediaItems;

        // Reset ordering.
        newMedias.sort(function (a, b) {
          return a.dataset.mediaIndex - b.dataset.mediaIndex;
        });

        newMedias.some((media, index) => {
          if (media.dataset.mediaId == variant.featured_media.id) {
            const [element] = newMedias.splice(index, 1);
            newMedias.unshift(element);
            return true;
          }
        });

        this.elements.mediaList.innerHTML = "";
        newMedias.forEach((media) => {
          this.elements.mediaList.appendChild(media);
        });

        if (!FoxTheme.config.mqlMobile && this.context !== "quickview") {
          const selectedMedia = this.querySelector(
            `[data-media-id="${variant.featured_media.id}"]`,
          );
          if (selectedMedia) {
            window.scrollTo({
              top: selectedMedia.offsetTop,
              behavior: "smooth",
            });
          }
        }
      }
    },
  );
}
